//=============================================================================
//                       <--- Developed By Icon Team --->
//=============================================================================

import React, { useState, useContext } from "react";
import * as moment from "moment";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

// custom pages
import { styles } from "./styles";
import KpiReport from "apis/Report/Report";
import Context from "context/context";

// material-ui
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";


//=============================================================================
// First report year
//=============================================================================

const FIRST_REPORT_YEAR = 2024;


//=============================================================================
// Available years
//=============================================================================

function getAvailableYears() {
    const currentYear = new Date().getFullYear();

    const years = [];

    for (
        let year = currentYear;
        year >= FIRST_REPORT_YEAR;
        year--
    ) {
        years.push(year);
    }

    return years;
}


//=============================================================================
// Excel headers - match the 7 columns the backend actually returns
//=============================================================================

const HEADERS = [
    "Month",
    "Starting Unlock Subscribers",
    "New Unlock Subscribers",
    "Ending Unlock Subscribers",
    "Net Unlock Change",
    "Monthly Growth (%)",
    "% of New Enrollments with Unlock",
];

// Excel column letters for the two percentage fields, given the 7 headers above:
// A=Month B=Starting C=New D=Ending E=NetChange F=MonthlyGrowth% G=PctNewEnrollmentsWithUnlock
const PERCENTAGE_COLUMNS = ["F", "G"];

const COLUMN_WIDTHS = [
    { wch: 12 }, // Month
    { wch: 24 }, // Starting Unlock Subscribers
    { wch: 20 }, // New Unlock Subscribers
    { wch: 22 }, // Ending Unlock Subscribers
    { wch: 18 }, // Net Unlock Change
    { wch: 18 }, // Monthly Growth (%)
    { wch: 30 }, // % of New Enrollments with Unlock
];


//=============================================================================
// Component
//=============================================================================

export default function UnlockTrendAnalysis() {

    const { state, dispatch } = useContext(Context);

    const { UnlockTrendAPI } = KpiReport();

    const classes = styles();

    const availableYears = getAvailableYears();


    //=========================================================================
    // State
    //=========================================================================

    // No default year
    const [selectedYear, setSelectedYear] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);

    const [loading, setLoading] = useState(false);


    //=========================================================================
    // Select Year
    //=========================================================================

    const handleYearSelect = (year) => {

        setSelectedYear(year);

        setAnchorEl(null);
    };


    //=========================================================================
    // Export Excel
    //=========================================================================

    const handleExport = async () => {

        //=====================================================================
        // Validate year
        //=====================================================================

        if (!selectedYear) {

            toast.error(
                "Please select a year before exporting the report.",
                {
                    toastId:
                        "unlock-trend-year-required",
                }
            );

            return;
        }


        setLoading(true);


        try {

            //=================================================================
            // Backend payload
            //=================================================================

            const payload = {
                year: Number(selectedYear),
            };


            //=================================================================
            // API call
            //=================================================================

            const response =
                await UnlockTrendAPI(payload);


            //=================================================================
            // Handle error
            //=================================================================

            if (
                response?.type === "ERROR" ||
                response?.success === false
            ) {

                toast.error(
                    response?.message ||
                    "Unable to export the report.",
                    {
                        toastId:
                            "unlock-trend-export-error",
                    }
                );

                return;
            }


            //=================================================================
            // Get report data
            //=================================================================

            const report =
                response?.data;


            if (
                !report ||
                !report.months ||
                report.months.length === 0
            ) {

                toast.info(
                    "No data found for the selected year.",
                    {
                        toastId:
                            "unlock-trend-no-data",
                    }
                );

                return;
            }


            //=================================================================
            // Excel rows - field names matching the actual getUnlockTrend response
            //=================================================================

            const rows = report.months.map((month) => {

                return [
                    month.month
                        ? moment(month.month).format("MMM-YY")
                        : "",

                    month.startingUnlockSubscribers ?? "",

                    month.newUnlockSubscribers ?? "",

                    month.endingUnlockSubscribers ?? "",

                    month.netUnlockChange ?? "",

                    month.monthlyGrowthPct ?? null,

                    month.pctNewEnrollmentsWithUnlock ?? null,
                ];
            });


            //=================================================================
            // Complete Excel data
            //=================================================================

            const excelData = [
                HEADERS,
                ...rows,
            ];


            //=================================================================
            // Create worksheet
            //=================================================================

            const worksheet =
                XLSX.utils.aoa_to_sheet(
                    excelData
                );


            //=================================================================
            // Column widths
            //=================================================================

            worksheet["!cols"] = COLUMN_WIDTHS;


            //=================================================================
            // Percentage columns
            //
            // Excel columns:
            //
            // F = Monthly Growth (%)
            // G = % of New Enrollments with Unlock
            //=================================================================

            report.months.forEach(
                (month, index) => {

                    const excelRow =
                        index + 2;


                    PERCENTAGE_COLUMNS.forEach(
                        (column) => {

                            const cell =
                                worksheet[
                                    `${column}${excelRow}`
                                ];


                            if (
                                cell &&
                                cell.v !== null &&
                                cell.v !== undefined
                            ) {

                                cell.z = "0.00%";
                            }
                        }
                    );
                }
            );


            //=================================================================
            // Description sheet - explains each column's formula
            //=================================================================

            const descriptionRows = [
                ["Column", "Formula"],
                ["Starting Unlock Subscribers", "Previous month's Ending Unlock Subscribers"],
                ["New Unlock Subscribers", "Customers who obtained Unlock for the first time during the month"],
                ["Ending Unlock Subscribers", "Starting Unlock Subscribers + New Unlock Subscribers"],
                ["Net Unlock Change", "Ending Unlock Subscribers - Starting Unlock Subscribers"],
                ["Monthly Growth (%)", "Net Unlock Change / Starting Unlock Subscribers x 100"],
                ["% of New Enrollments with Unlock", "New Unlock Subscribers / New Enrollments x 100"],
            ];

            const descriptionSheet = XLSX.utils.aoa_to_sheet(descriptionRows);

            descriptionSheet["!cols"] = [
                { wch: 32 },
                { wch: 70 },
            ];


            //=================================================================
            // Create workbook
            //=================================================================

            const workbook =
                XLSX.utils.book_new();


            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Thrive Unlocked Trend Analysis"
            );

            XLSX.utils.book_append_sheet(
                workbook,
                descriptionSheet,
                "Description"
            );


            //=================================================================
            // File name
            //=================================================================

            const fileName =
                `Unlock Trend Analysis Report ${selectedYear} ${moment(
                    new Date()
                ).format(
                    "MMM DD YYYY"
                )}.xlsx`;


            //=================================================================
            // Download Excel
            //=================================================================

            XLSX.writeFile(
                workbook,
                fileName
            );


            //=================================================================
            // Success toast
            //=================================================================

            toast.success(
                `Unlock Trend Analysis report downloaded for ${selectedYear}.`,
                {
                    toastId:
                        "unlock-trend-export-success",
                }
            );

        } catch (error) {

            console.error(
                "Unlock Trend Export Error:",
                error
            );

            toast.error(
                "Unable to download the report right now.",
                {
                    toastId:
                        "unlock-trend-export-error",
                }
            );

        } finally {

            setLoading(false);
        }
    };


    //=========================================================================
    // UI
    //=========================================================================

    return (
        <div>

            <div
                className={classes.exportAndPreview}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 16,
                    marginTop: 20,
                    marginBottom: 20,
                }}
            >

                {/*=============================================================
                    Select Year - solid navy pill, no white split
                =============================================================*/}

                <div
                    onClick={(event) =>
                        setAnchorEl(
                            event.currentTarget
                        )
                    }
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minWidth: 260,
                        height: 46,
                        borderRadius: 23,
                        border: "1px solid #E0E0E0",
                        overflow: "hidden",
                        backgroundColor: "#FFFFFF",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        cursor: "pointer",
                    }}
                >

                    {/* Label half - same blue as Export Excel */}
                    <div
                        style={{
                            backgroundColor: "#3E5C94",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: 15,
                            letterSpacing: 0.2,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 22px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Select Year
                    </div>

                    {/* White value half - centered text, arrow pinned to the right */}
                    <div
                        style={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: selectedYear ? "#333" : "#999",
                            fontSize: 15,
                        }}
                    >
                        <span>
                            {selectedYear ? selectedYear : ""}
                        </span>
                        <ArrowDropDownIcon
                            style={{
                                position: "absolute",
                                right: 14,
                                color: "#8A8A8A",
                            }}
                        />
                    </div>

                </div>


                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() =>
                        setAnchorEl(null)
                    }
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    PaperProps={{
                        style: {
                            marginTop: 4,
                            minWidth: 220,
                        },
                    }}
                >

                    {availableYears.map(
                        (year) => (

                            <MenuItem
                                key={year}
                                selected={
                                    year ===
                                    selectedYear
                                }
                                onClick={() =>
                                    handleYearSelect(
                                        year
                                    )
                                }
                            >
                                {year}
                            </MenuItem>

                        )
                    )}

                </Menu>


                {/*=============================================================
                    Export Excel - stacked below the year selector
                =============================================================*/}

                <Button
                    variant="contained"
                    className={classes.exportBtn}
                    onClick={handleExport}
                    disabled={loading}
                    disableElevation
                    style={{
                        minWidth: 180,
                        height: 46,
                        borderRadius: 8,
                        backgroundColor: "#3E5C94",
                        color: "#FFFFFF",
                        textTransform: "none",
                        fontSize: 15,
                        fontWeight: 500,
                        letterSpacing: 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }}
                >

                    {loading
                        ? "Exporting..."
                        : "Export Excel"}

                </Button>

            </div>

        </div>
    );
}